<script setup lang="ts">
import type { Item } from './SavedAnalysisStateBrowser.vue';
import type { BatchResult } from '@/src/components/segmentation/segmentation-typing';
import type { Json } from '@/supabase/types';

import { useActiveStudyStore } from '@src/store/active-study';
import { seriesClassification } from '@src/store/dicom-web/SeriesClassification';
import LZString from 'lz-string';
import { ref, useTemplateRef, watch } from 'vue';

import { useAlert } from '@/src/composables/useAlert';
import { useBackendSegmentation } from '@/src/composables/useBackendSegmentation';
import { useSegmentationDialog } from '@/src/composables/useSegmentationDialog';
import { useSegmentationGateway } from '@/src/composables/useSegmentationGateway';
import { session, supabase } from '@/src/supabase';

import { SerializedAnalysisStateSchema, useAppState } from '../store/ocg-app-state';
import { getMessage } from '../utils/exception';
import SavedAnalysisStateBrowser from './SavedAnalysisStateBrowser.vue';

const SYSTEM_SAVE_VERSION = 'system-auto-v1';
const SAVE_WITH_RESULT_TITLE = 'Completed Analysis';
const AUTOSAVE_TITLE = 'Background AutoSave';
const SYSTEM_USER_ID = '7fd2b0e5-17b4-433c-9f45-93a5243d452e'; // TODO! move to .env or config

const appState = useAppState();
const alert = useAlert();
const activeStudyStore = useActiveStudyStore();
const segDialog = useSegmentationDialog();

const browser
  = useTemplateRef<InstanceType<typeof SavedAnalysisStateBrowser>>('browser');
const showDialog = ref(false);
const saveMode = ref(false);
const studyInstanceUid = ref('');
const activeTab = ref<'analysis' | 'jobs'>('analysis');
const askConfirmationOverwrite = ref(false);
const askConfirmationLoad = ref(false);
const askConfirmationDelete = ref(false);

const idToConfirm = ref<string | null>(null);
const showSaveWarningDialog = ref(false);
const targetElement = ref('');
let warningResolve: ((value: boolean) => void) | null = null;
let warningReject: (() => void) | null = null;
const newTitle = ref('');
let resolve: ((id: string) => void) | null = null;
let reject: (() => void) | null = null;

watch(
  () => segDialog.showJobsHub.value,
  (show) => {
    if (show) {
      activeTab.value = 'jobs';
      showDialog.value = true;
      void segDialog.fetchJobsList();
    }
  },
);

watch(
  () => [showDialog.value, activeTab.value],
  ([show, tab]) => {
    if (show) {
      if (tab === 'jobs') {
        void segDialog.fetchJobsList();
      }
      else if (tab === 'analysis') {
        void browser.value?.reload();
      }
    }
  },
);

async function invokeSave(_studyInstanceUid: string) {
  studyInstanceUid.value = _studyInstanceUid;
  saveMode.value = true;
  newTitle.value = '';
  activeTab.value = 'analysis';
  showDialog.value = true;
  void browser.value?.reload();
  return new Promise<string>((res, rej) => {
    resolve = res;
    reject = rej;
  });
}

async function invokeLoad(_studyInstanceUid: string) {
  studyInstanceUid.value = _studyInstanceUid;
  saveMode.value = false;
  activeTab.value = 'analysis';
  showDialog.value = true;
  void browser.value?.reload();
  return new Promise<string>((res, rej) => {
    resolve = res;
    reject = rej;
  });
}

function invokeJobs(_studyInstanceUid?: string) {
  if (_studyInstanceUid) {
    studyInstanceUid.value = _studyInstanceUid;
  }
  activeTab.value = 'jobs';
  showDialog.value = true;
  void segDialog.fetchJobsList();
}

async function invokeSaveWarning(
  _targetElement: string,
  _studyInstanceUid: string,
) {
  targetElement.value = _targetElement;
  studyInstanceUid.value = _studyInstanceUid;
  showSaveWarningDialog.value = true;
  return new Promise<boolean>((res, rej) => {
    warningResolve = res;
    warningReject = rej;
  });
}

function resolveDialog(id: string) {
  showDialog.value = false;
  saveMode.value = false;
  segDialog.showJobsHub.value = false;
  resolve?.(id);
  resolve = null;
  reject = null;
}

function cancelDialog() {
  showDialog.value = false;
  saveMode.value = false;
  segDialog.showJobsHub.value = false;
  reject?.();
  resolve = null;
  reject = null;
}

async function handleRowClick(item: Item) {
  idToConfirm.value = item.id;
  if (saveMode.value) {
    if (session.value?.user.id && item.user !== session.value.user.id) {
      alert.error('You cannot overwrite another user\'s analysis state.');
      return;
    }
    newTitle.value = item.title;
    askConfirmationOverwrite.value = true;
    return;
  }

  // Load mode
  const isOwner = !!(session.value?.user.id && item.user === session.value.user.id);
  if (!isOwner) {
    const userConfirm = await alert.confirm(
      'You are loading an analysis state for which you are not the owner. This will create a copy of that state so you can continue your work. Do you want to continue?',
      'Confirm',
      'warning',
    );
    if (userConfirm) {
      await duplicateAndLoadAnalysisState(item.id);
    }
    return;
  }

  askConfirmationLoad.value = true;
}

async function handleClickDelete(item: Item) {
  if (session.value?.user.id && item.user !== session.value.user.id) {
    alert.error('You cannot delete another user\'s analysis state.');
    return;
  }
  idToConfirm.value = item.id;
  askConfirmationDelete.value = true;
}

async function storeContours(
  analysisStateID: string,
  contourContainerStore: any,
) {
  const imageUIDs = Object.keys(contourContainerStore);
  const userID = session.value?.user.id;
  if (!userID) {
    alert.error('No user logged in');
    return;
  }
  imageUIDs.forEach((imageUID) => {
    const contours = contourContainerStore[imageUID];
    const contoursJson = JSON.stringify(contours);
    const compressed = LZString.compressToUTF16(contoursJson);

    supabase
      .from('analysis_state_contour')
      .insert({
        analysis_state_id: analysisStateID,
        image_instance_uid: imageUID,
        contours: compressed,
      })
      .select('id')
      .single()
      .then((result) => {
        if (result.error) {
          alert.error(
            `Error saving contour for ${imageUID}: ${getMessage(result.error)}`,
          );
        }
      });
  });
}

interface SaveStateOptions {
  title: string;
  studyInstanceUid?: string;
  targetId?: string | null;
  upsertByTitle?: boolean;
  silent?: boolean;
}

async function executeSaveState(options: SaveStateOptions): Promise<string> {
  const targetStudyUid = options.studyInstanceUid || studyInstanceUid.value;
  if (!session.value) {
    throw new Error('No user logged in');
  }
  if (!targetStudyUid) {
    throw new Error('No study instance UID specified');
  }

  const analysisState = appState.serializeAnalysisState(options.title);
  const { contourContainerStore, analysisResult, ...rawState } = analysisState;

  const state = {
    ...rawState,
    hasResults: !!analysisResult,
  };

  let targetSaveId: string | null = options.targetId ?? null;

  if (!targetSaveId && options.upsertByTitle) {
    const { data: existingSaves, error: searchError } = await supabase
      .from('analysis_state')
      .select('id')
      .eq('study_instance_uid', targetStudyUid)
      .eq('user', session.value.user.id)
      .eq('state->>title', options.title)
      .limit(1);

    if (searchError) throw searchError;
    if (existingSaves && existingSaves.length > 0) {
      targetSaveId = existingSaves[0].id;
    }
  }

  let finalAnalysisStateID = '';

  if (targetSaveId) {
    // Verify targetSaveId belongs to current user
    const { data: targetCheck, error: checkError } = await supabase
      .from('analysis_state')
      .select('user')
      .eq('id', targetSaveId)
      .single();

    if (checkError || !targetCheck) {
      throw new Error('Analysis state to overwrite not found');
    }
    if (targetCheck.user !== session.value.user.id) {
      throw new Error('You cannot modify another user\'s analysis state.');
    }

    // Delete existing contours for this analysis state
    const { error: deleteContourError } = await supabase
      .from('analysis_state_contour')
      .delete()
      .eq('analysis_state_id', targetSaveId);
    if (deleteContourError) throw deleteContourError;

    // Delete existing results for this analysis state (if any)
    const { error: deleteResultError } = await supabase
      .from('analysis_state_result')
      .delete()
      .eq('analysis_state_id', targetSaveId);
    if (deleteResultError) throw deleteResultError;

    // Update the analysis state
    const { error: updateError } = await supabase
      .from('analysis_state')
      .update({
        state: state as unknown as Json,
        created_at: new Date().toISOString(),
      })
      .eq('id', targetSaveId);
    if (updateError) throw updateError;

    finalAnalysisStateID = targetSaveId;
  }
  else {
    // Create new analysis state row
    const { data, error } = await supabase
      .from('analysis_state')
      .insert({
        org: (appState.currentUserOrgs as any[])[0]?.id,
        user: session.value.user.id,
        study_instance_uid: targetStudyUid,
        state: state as unknown as Json,
      })
      .select('id')
      .single();

    if (error) throw error;
    if (!data) throw new Error('No data returned from save insert');
    finalAnalysisStateID = data.id;
  }

  // Store contours
  storeContours(finalAnalysisStateID, contourContainerStore);

  // If analysis result exists, store in analysis_state_result table
  if (analysisResult) {
    const { error: resultError } = await supabase
      .from('analysis_state_result')
      .insert({
        analysis_state_id: finalAnalysisStateID,
        study_ocg_result: analysisResult,
      });
    if (resultError) throw resultError;
  }

  activeStudyStore.hasSavedState = true;

  if (!options.silent) {
    if (targetSaveId) {
      alert.info(`Successfully overwrote save: "${options.title}"`);
    }
    else {
      alert.info(`Successfully saved analysis state: "${options.title}"`);
    }
  }

  return finalAnalysisStateID;
}

async function saveWithTitle(title: string) {
  return await executeSaveState({
    title,
    silent: false,
  });
}

async function newNameEntered() {
  if (newTitle.value.trim() === '' || !session.value) {
    return;
  }
  try {
    const analysisStateID = await saveWithTitle(newTitle.value.trim());
    resolveDialog(analysisStateID);
  }
  catch (error) {
    alert.error(`Error saving analysis state: ${getMessage(error)}`);
  }
}

async function confirmOverwrite() {
  askConfirmationOverwrite.value = false;
  if (idToConfirm.value === null) {
    return;
  }
  try {
    const analysisStateID = await executeSaveState({
      title: newTitle.value.trim(),
      targetId: idToConfirm.value,
      silent: false,
    });
    resolveDialog(analysisStateID);
  }
  catch (error) {
    alert.error(`Error saving analysis state: ${getMessage(error)}`);
  }
}

async function loadGeneralAnalysisState(analysisStateID: string) {
  if (!analysisStateID) {
    return;
  }
  alert.info('Saved state found, loading...');
  try {
    // Load the analysis state
    const { data, error } = await supabase
      .from('analysis_state')
      .select('state')
      .eq('id', analysisStateID)
      .single();
    if (error) throw error;
    const { state } = data;
    if (!state) {
      throw new Error('Analysis state data is null');
    }

    // Load the contours for user saves
    const { data: contoursData, error: contoursError } = await supabase
      .from('analysis_state_contour')
      .select('image_instance_uid, contours')
      .eq('analysis_state_id', analysisStateID);
    if (contoursError) throw contoursError;

    // Reconstruct the contourContainerStore
    const contourContainerStore: Record<string, any> = {};
    contoursData?.forEach((row) => {
      if (!row.image_instance_uid) {
        alert.warning('Missing image_instance_uid in contour data row');
        return;
      }
      if (!row.contours) {
        alert.warning(
          `Contour data is null for image ${row.image_instance_uid}`,
        );
        return;
      }
      const decompressed = LZString.decompressFromUTF16(row.contours);
      if (decompressed === null) {
        throw new Error(
          `Failed to decompress contours for image ${row.image_instance_uid}`,
        );
      }
      contourContainerStore[row.image_instance_uid] = JSON.parse(decompressed);
    });

    let analysisResult = null;

    if ((state as any).hasResults) {
      const { data: resultData, error: resultError } = await supabase
        .from('analysis_state_result')
        .select('study_ocg_result')
        .eq('analysis_state_id', analysisStateID)
        .single();
      if (resultError) throw resultError;
      if (resultData) {
        analysisResult = resultData.study_ocg_result;
      }
    }

    // Combine state with contourContainerStore
    appState.loadAnalysisState(SerializedAnalysisStateSchema.parse({
      ...(data.state as Record<string, any>),
      contourContainerStore,
      analysisResult, // Placeholder, will be populated below if exists
    }));

    activeStudyStore.hasSavedState = true;
    alert.info(`Successfully loaded save.`);
  }
  catch (error) {
    alert.error(`Error fetching analysis state: ${getMessage(error)}`);
  }
}

// Ideally moved to appState.loadAnalysisState. Need to keep here for now
// as a system save does not have all the bells and whistles of a regular save
// TODO: fix in future
async function loadClassification(targetID: string) {
  try {
    // Also fetch classification results
    const { data, error } = await supabase
      .from('analysis_state')
      .select('state')
      .eq('id', targetID)
      .single();
    if (error) {
      alert.error(`Error fetching analysis state: ${getMessage(error)}`);
      return;
    }

    const { state } = data;
    const { classification } = state as any;
    // Backend stores the classification information based on seriesInstanceUID. Front-end based on seriesIdx.
    // To prevent missmatch (especially for multiframe), we need to remap here.
    seriesClassification.adjustClassification(classification);
    seriesClassification.applyDefaultViewportMapping();
  }
  catch (error) {
    alert.error(`Error applying classification: ${getMessage(error)}`);
  }
}

// TODO: This is redundant and should be merged to lead a state, currently system contours do not have
// all the info that comes from contourContainerStore.deserialize so keeping to avoid breaking changes, but
//  should unify the loading process for system and user saves
async function loadSystemAnalysisState(studyInstanceUID?: string) {
  if (!studyInstanceUID && idToConfirm.value === null) {
    return;
  }
  // If the user has not provided a studyInstanceUID, use the local idToConfirm
  let targetId: string | null = '';
  if (
    studyInstanceUID === undefined
    || studyInstanceUID === ''
    || studyInstanceUID === null
  ) {
    targetId = idToConfirm.value;
  }
  else {
    if (session.value) {
      const { data: autoSaves } = await supabase
        .from('analysis_state')
        .select('id')
        .eq('study_instance_uid', studyInstanceUID)
        .eq('user', session.value.user.id)
        .eq('state->>title', AUTOSAVE_TITLE)
        .order('created_at', { ascending: false })
        .limit(1);

      if (autoSaves && autoSaves.length > 0) {
        targetId = autoSaves[0].id;
        await loadGeneralAnalysisState(targetId);
        return;
      }
    }

    // Fetch the latest system save for the given studyInstanceUID
    const { data, error } = await supabase
      .from('analysis_state')
      .select('id')
      .eq('study_instance_uid', studyInstanceUID)
      .eq('user', SYSTEM_USER_ID) // System User ID
      .order('created_at', { ascending: false })
      .limit(1)
      .single();
    if (error || !data) {
      alert.warning(`No system save found for the given study.`);
      return;
    }
    targetId = data.id;
  }

  if (!targetId) {
    alert.info('No existing system analysis state found.');
    return;
  }

  alert.info('System save found, loading contours...');

  await loadClassification(targetId);

  try {
    // Load the contours for system save
    const { data: contoursData, error: contoursError } = await supabase
      .from('analysis_state_contour')
      .select('image_instance_uid, contours')
      .eq('analysis_state_id', targetId);
    if (contoursError) throw contoursError;

    // Reconstruct contours as batches for system saves
    const allContoursBatches: BatchResult[] = [];
    contoursData?.forEach((row) => {
      if (!row.image_instance_uid) {
        alert.warning('Missing image_instance_uid in contour data row');
        return;
      }
      if (!row.contours) {
        alert.warning(
          `Contour data is null for image ${row.image_instance_uid}`,
        );
        return;
      }
      const decompressed = LZString.decompressFromUTF16(row.contours);
      if (decompressed === null) {
        throw new Error(
          `Failed to decompress contours for image ${row.image_instance_uid}`,
        );
      }
      allContoursBatches.push({
        imageUID: row.image_instance_uid,
        contours: JSON.parse(decompressed),
      });
    });

    const segAgent = useBackendSegmentation();
    segAgent.addContourBatch(allContoursBatches);

    resolveDialog(targetId);

    activeStudyStore.hasSavedState = true;
    alert.info(`Successfully loaded system save.`);
  }
  catch (error) {
    alert.error(`Error loading system analysis state: ${getMessage(error)}`);
  }
}

async function duplicateAndLoadAnalysisState(sourceAnalysisStateID: string) {
  if (!session.value?.user.id) {
    alert.error('No user logged in');
    return;
  }
  const currentUserId = session.value.user.id;

  try {
    alert.info('Creating a copy of the analysis state...');

    // 1. Fetch source analysis state
    const { data: sourceState, error: stateError } = await supabase
      .from('analysis_state')
      .select('*')
      .eq('id', sourceAnalysisStateID)
      .single();

    if (stateError || !sourceState) {
      throw new Error(`Failed to fetch source analysis state: ${getMessage(stateError)}`);
    }

    // 2. Fetch contours of source
    const { data: sourceContours, error: contoursError } = await supabase
      .from('analysis_state_contour')
      .select('image_instance_uid, contours')
      .eq('analysis_state_id', sourceAnalysisStateID);

    if (contoursError) {
      throw new Error(`Failed to fetch contours: ${getMessage(contoursError)}`);
    }

    // 3. Fetch result of source (if any)
    const { data: sourceResult, error: resultError } = await supabase
      .from('analysis_state_result')
      .select('study_ocg_result, study_ocg_screenshot')
      .eq('analysis_state_id', sourceAnalysisStateID)
      .maybeSingle();

    if (resultError) {
      throw new Error(`Failed to fetch analysis result: ${getMessage(resultError)}`);
    }

    // 4. Create new state payload with current user as owner
    const rawState = (sourceState.state as Record<string, any>) || {};
    const originalTitle = rawState.title || 'Analysis State';
    const newStateData = {
      ...rawState,
      title: originalTitle,
      createdBy: {
        id: currentUserId,
        card: appState.currentUser || { fn: session.value.user.email || 'User' },
      },
      createdAt: new Date().toISOString(),
    };

    // 5. Insert new analysis_state row with current user as owner
    const userOrgId = (appState.currentUserOrgs as any[])[0]?.id || sourceState.org;
    const { data: newAnalysisState, error: insertStateError } = await supabase
      .from('analysis_state')
      .insert({
        org: userOrgId,
        user: currentUserId,
        study_instance_uid: sourceState.study_instance_uid,
        state: newStateData as unknown as Json,
      })
      .select('id')
      .single();

    if (insertStateError || !newAnalysisState) {
      throw new Error(`Failed to copy analysis state: ${getMessage(insertStateError)}`);
    }

    const newId = newAnalysisState.id;

    // 6. Insert duplicate contours for the new analysis state
    if (sourceContours && sourceContours.length > 0) {
      const contoursToInsert = sourceContours.map(c => ({
        analysis_state_id: newId,
        image_instance_uid: c.image_instance_uid,
        contours: c.contours,
      }));

      const { error: insertContoursError } = await supabase
        .from('analysis_state_contour')
        .insert(contoursToInsert);

      if (insertContoursError) {
        throw new Error(`Failed to copy contours: ${getMessage(insertContoursError)}`);
      }
    }

    // 7. Insert duplicate analysis result if exists
    if (sourceResult) {
      const { error: insertResultError } = await supabase
        .from('analysis_state_result')
        .insert({
          analysis_state_id: newId,
          study_ocg_result: sourceResult.study_ocg_result,
          study_ocg_screenshot: sourceResult.study_ocg_screenshot,
        });

      if (insertResultError) {
        throw new Error(`Failed to copy analysis result: ${getMessage(insertResultError)}`);
      }
    }

    // 8. Now load the new copy into memory
    const isSystemSave = rawState.appVersion === SYSTEM_SAVE_VERSION;
    if (isSystemSave) {
      idToConfirm.value = newId;
      await loadSystemAnalysisState();
      idToConfirm.value = null;
    }
    else {
      await loadGeneralAnalysisState(newId);
    }

    resolveDialog(newId);
    activeStudyStore.hasSavedState = true;
    browser.value?.reload();
    alert.success('Analysis state copied and loaded successfully.');
  }
  catch (error) {
    console.error('Error duplicating and loading analysis state:', error);
    alert.error(`Failed to copy and load state: ${getMessage(error)}`);
  }
}

async function confirmLoad() {
  askConfirmationLoad.value = false;
  if (idToConfirm.value === null) {
    return;
  }
  const targetId = idToConfirm.value;
  idToConfirm.value = null;

  try {
    const { data, error } = await supabase
      .from('analysis_state')
      .select('state')
      .eq('id', targetId)
      .single();
    if (error) throw error;

    const isSystemSave = (data?.state as any)?.appVersion === SYSTEM_SAVE_VERSION;
    if (isSystemSave) {
      idToConfirm.value = targetId;
      await loadSystemAnalysisState();
      idToConfirm.value = null;
      return;
    }

    await loadGeneralAnalysisState(targetId);
    resolveDialog(targetId);
  }
  catch (error) {
    alert.error(`Error loading analysis state: ${getMessage(error)}`);
  }
}

async function confirmDelete() {
  askConfirmationDelete.value = false;
  if (idToConfirm.value === null || !session.value) {
    return;
  }
  try {
    const { count, error } = await supabase
      .from('analysis_state')
      .delete({ count: 'exact' })
      .eq('id', idToConfirm.value)
      .eq('user', session.value.user.id);
    if (error) throw error;
    if (count === 0) throw new Error('No rows deleted or not authorized');
  }
  catch (error) {
    alert.error(`Error deleting analysis state: ${getMessage(error)}`);
    return;
  }
  alert.info(`Successfully deleted save.`);

  browser.value?.reload();
}

function cancelWarning() {
  showSaveWarningDialog.value = false;
  warningReject?.();
  warningResolve = null;
  warningReject = null;
}

async function continueWithoutSaving() {
  showSaveWarningDialog.value = false;
  warningResolve?.(true);
  warningResolve = null;
  warningReject = null;
}

async function createAutoSave() {
  showSaveWarningDialog.value = false;
  try {
    await backgroundAutosave();
    warningResolve?.(true);
    alert.info(`Successfully created autosave.`);
  }
  catch (error) {
    alert.error(`Error auto-saving: ${getMessage(error)}`);
    warningReject?.();
  }
  finally {
    warningResolve = null;
    warningReject = null;
  }
}

async function backgroundAutosave(targetStudyInstanceUid?: string) {
  if (targetStudyInstanceUid) {
    studyInstanceUid.value = targetStudyInstanceUid;
  }
  if (!session.value || !studyInstanceUid.value) {
    console.warn('Cannot autosave: no session or study instance UID');
    return;
  }

  try {
    const analysisState = appState.serializeAnalysisState(AUTOSAVE_TITLE);
    const hasResults = !!analysisState.analysisResult;

    const title = hasResults ? SAVE_WITH_RESULT_TITLE : AUTOSAVE_TITLE;

    await executeSaveState({
      title,
      studyInstanceUid: studyInstanceUid.value,
      upsertByTitle: true,
      silent: true,
    });
  }
  catch (error) {
    console.error('Background autosave failed:', error);
    // Don't show user alerts for background saves to avoid interrupting workflow
  }
}

async function loadLatestSave(studyInstanceUID: string) {
  if (!studyInstanceUID) return;

  const currentUserId = session.value?.user?.id || (await supabase.auth.getSession()).data.session?.user?.id;
  if (!currentUserId) {
    console.warn('[loadLatestSave] No logged-in user, skipping auto-load.');
    return;
  }

  try {
    // Case 1 & Case 2: Check if current user has an analysis_state save under self.
    // Prioritize loading self state even if other users have more recent saves.
    const { data: userSaves, error: userSaveError } = await supabase
      .from('analysis_state')
      .select('id, state')
      .eq('study_instance_uid', studyInstanceUID)
      .eq('user', currentUserId)
      .order('created_at', { ascending: false })
      .limit(1);

    if (userSaveError) throw userSaveError;

    if (userSaves && userSaves.length > 0) {
      const { id, state } = userSaves[0];
      const isSystemSave = (state as any)?.appVersion === SYSTEM_SAVE_VERSION;

      if (isSystemSave) {
        idToConfirm.value = id;
        await loadSystemAnalysisState();
        idToConfirm.value = null;
      }
      else {
        await loadGeneralAnalysisState(id);
      }
      return;
    }

    // Case 3: Current user does NOT have an analysis_state save under self.
    // Check if there are existing saves from other users.
    const { count: otherUsersSavesCount, error: countError } = await supabase
      .from('analysis_state')
      .select('id', { count: 'exact', head: true })
      .eq('study_instance_uid', studyInstanceUID)
      .neq('user', currentUserId);

    if (countError) throw countError;

    if (otherUsersSavesCount && otherUsersSavesCount > 0) {
      // Saves from other users exist: do not auto-load other user save. Load nothing!
      console.log(
        `[loadLatestSave] User has no save, but ${otherUsersSavesCount} save(s) from other users exist. Not auto-loading.`,
      );
      return;
    }

    // No saves exist from any user: check fallback for finished init jobs
    const gateway = useSegmentationGateway();
    const jobsRes = await gateway.listJobs({
      studyInstanceUid: studyInstanceUID,
      status: 'complete',
    });

    const finishedInitJobs = (jobsRes.jobs || []).filter(
      job => job.type?.toLowerCase() === 'init' && job.status?.status === 'complete',
    );

    if (finishedInitJobs.length > 0) {
      finishedInitJobs.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
      const latestInitJob = finishedInitJobs[0];
      console.log(`[loadLatestSave] No analysis_state found. Fallback to finished init job: ${latestInitJob.job_id}`);
      await segDialog.applyGatewayJobResults(latestInitJob.job_id);
    }
  }
  catch (error) {
    console.error(`Error loading latest save: ${getMessage(error)}`);
  }
}

defineExpose({
  invokeSave,
  invokeLoad,
  invokeJobs,
  invokeSaveWarning,
  loadSystemAnalysisState,
  backgroundAutosave,
  loadLatestSave,
});
</script>

<template>
  <v-dialog
    v-model="showDialog"
    max-width="950"
    scrollable
    @update:model-value="showDialog || cancelDialog()"
  >
    <v-card>
      <div class="d-flex align-center justify-space-between px-4 pt-3 pb-1 border-b">
        <v-tabs v-model="activeTab" color="primary">
          <v-tab value="analysis">
            Analysis State
          </v-tab>
          <v-tab value="jobs">
            Backend Jobs
          </v-tab>
        </v-tabs>

        <v-btn
          v-if="activeTab === 'jobs'"
          variant="text"
          density="comfortable"
          :loading="segDialog.isLoadingJobs.value"
          @click="segDialog.fetchJobsList()"
        >
          Refresh
        </v-btn>
      </div>

      <v-card-text class="pt-4">
        <v-tabs-window v-model="activeTab">
          <v-tabs-window-item value="analysis">
            <div class="text-body-large font-weight-medium mb-3">
              {{
                saveMode
                  ? 'Save Analysis State: Enter new name or select an existing entry to overwrite'
                  : 'Choose analysis state to load'
              }}
            </div>
            <v-text-field
              v-if="saveMode"
              v-model="newTitle"
              clearable
              placeholder="New name"
              @keyup.enter="newNameEntered"
              @click:clear="newTitle = ''"
            />
            <SavedAnalysisStateBrowser
              ref="browser"
              :study-instance-uid="studyInstanceUid"
              @clicked="handleRowClick"
              @remove-clicked="handleClickDelete"
            />
          </v-tabs-window-item>

          <v-tabs-window-item value="jobs">
            <div class="text-body-large font-weight-medium mb-1">
              Backend Jobs Hub
            </div>
            <div class="text-body-small text-grey-darken-1 mb-4">
              Active and past backend requests.
            </div>

            <v-progress-linear
              v-if="segDialog.isLoadingJobs.value"
              indeterminate
              color="primary"
              class="mb-4"
            />

            <v-table v-if="segDialog.jobsList.value.length > 0" density="comfortable">
              <thead>
                <tr>
                  <th>Request Type</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Created At</th>
                  <th class="text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="job in segDialog.jobsList.value" :key="job.job_id">
                  <td>{{ job.type }}</td>
                  <td>
                    <v-chip
                      size="small"
                      :color="
                        job.status.status === 'complete' ? 'success'
                        : job.status.status === 'processing' ? 'warning'
                          : job.status.status === 'queued' ? 'info'
                            : 'error'
                      "
                      variant="flat"
                    >
                      {{ job.status.status }}
                    </v-chip>
                  </td>
                  <td style="min-width: 120px;">
                    <div class="d-flex align-center ga-2">
                      <v-progress-linear
                        :model-value="job.status.progress || 0"
                        color="primary"
                        height="8"
                        rounded
                        style="flex: 1"
                      />
                      <span class="text-body-small">{{ (job.status.progress || 0).toFixed(0) }}%</span>
                    </div>
                  </td>
                  <td class="text-body-small">
                    {{ new Date(job.created_at).toLocaleString() }}
                  </td>
                  <td class="text-right">
                    <v-btn
                      v-if="job.status.status === 'complete'"
                      color="success"
                      size="small"
                      variant="flat"
                      :loading="segDialog.applyingJobId.value === job.job_id"
                      @click="segDialog.applyGatewayJobResults(job.job_id)"
                    >
                      Apply
                    </v-btn>
                    <v-btn
                      v-else-if="job.status.status === 'queued' || job.status.status === 'processing'"
                      color="error"
                      size="small"
                      variant="tonal"
                      @click="segDialog.abortGatewayJob(job.job_id)"
                    >
                      Abort
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-table>

            <div v-else-if="!segDialog.isLoadingJobs.value" class="text-center py-6 text-body-medium text-grey">
              No segmentation jobs found.
            </div>
          </v-tabs-window-item>
        </v-tabs-window>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn v-if="activeTab === 'analysis' && saveMode" color="primary" @click="newNameEntered">
          Save
        </v-btn>
        <v-btn @click="cancelDialog">
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="askConfirmationOverwrite" max-width="50vw">
    <v-card>
      <v-card-title>Please confirm</v-card-title>
      <v-card-text>
        <p>Are you sure you want to overwrite the existing analysis state?</p>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn @click="askConfirmationOverwrite = false">
          Cancel
        </v-btn>
        <v-btn @click="confirmOverwrite">
          Overwrite
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-dialog v-model="askConfirmationLoad" max-width="50vw">
    <v-card>
      <v-card-title>Please confirm</v-card-title>
      <v-card-text>
        <p>Continue loading? This will replace your current analysis state.</p>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn @click="askConfirmationLoad = false">
          Cancel
        </v-btn>
        <v-btn @click="confirmLoad">
          Load
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-dialog v-model="askConfirmationDelete" max-width="50vw">
    <v-card>
      <v-card-title>Please confirm</v-card-title>
      <v-card-text>
        <p>Are you sure you want to delete this analysis state?</p>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn @click="askConfirmationDelete = false">
          Cancel
        </v-btn>
        <v-btn @click="confirmDelete">
          Delete
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-dialog v-model="showSaveWarningDialog" max-width="50vw">
    <v-card>
      <v-card-title>Please confirm</v-card-title>
      <v-card-text>
        <p>
          You have unsaved changes in {{ targetElement }}. We would recommend
          saving your work before continuing.
        </p>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn @click="cancelWarning">
          Cancel
        </v-btn>
        <v-btn @click="continueWithoutSaving">
          Continue Without Saving
        </v-btn>
        <v-btn @click="createAutoSave">
          Create AutoSave
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped></style>
